import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';

import { AppBarWithButtons } from '../../../components/designe/app-bars/app-bar-with-action-buttons/AppBarWithButtons';
import AppUserAvatar from '../../../components/designe/app-entity-avatar/app-user-avatar/AppUserAvatar';
import AppSwitchWithLabel from '../../../components/designe/app-switch-with-label/AppSwitchWithLabel';
import AppDashedBlockButton from '../../../components/designe/buttons/block-buttons/AppDashedBlockButton';
import AppFormDateTimePicker from '../../../components/designe/forms/app-form-date-time-picker';
import AppFormSelectWrapper from '../../../components/designe/forms/app-form-select-wrapper/AppFormSelectWrapper';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import SavedProgramSetSuperset from '../../../components/reusable/saved-program-set-superset-modal/SavedProgramSetSuperset';
import SetGroupEditTable from '../../../components/reusable/set-group-edit-table/SetGroupEditTable';
import TrainingActivityTypePicker from '../../../components/reusable/training-activity-type-picker';
import withRouter from '../../../hoc/with-router';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import {
  AvatarSize,
  AvatarTypes,
  TrainingStatus,
} from '../../../shared/constants';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { IUser } from '../../../types/interfaces/user';
import { getUserViewName } from '../../../utils/common/shared-util';
import { getSetKey } from '../../../utils/training/set-utils';
import { useStyles } from './EditTraining.styles';
import useEditTable from './useEditTable';
import { useEditTrainingPage } from './useEditTrainingPage';

function EditTraining(props) {
  const { params } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  const { classes } = useStyles(theme);

  const {
    formik,
    isLoading,
    isSubmitDisabled,
    training,
    user,
    canCreateForOthers,
    canChangeOwner,
    performerSelectOptions,
    onCreateForOthersChange,
    exerciseResults,
    onChangeTrainingSet,
    onAddEmptyTrainingSet,
    onRemoveTrainingSet,
    onCancelClick,
    trainingSetGroups,
    getSetIndex,
    showSavedTrainingModal,
    setShowSavedTrainingModal,
    onAddTrainingSetFromSavedProgramSet,
    onChangeSetOrder,
  } = useEditTrainingPage(params.trainingId);
  const { allowAndLeave, dialog } = useUnsavedChangesGuard({
    isDirty: formik.dirty && !formik.isSubmitting,
    leaveAction: onCancelClick,
  });

  const { showUploadSetButton } = useUserSetPreferences();
  const useSetGroupEditTable = useEditTable(formik, {
    exerciseResults,
  });

  useScrollToFirstError(formik, {
    fieldOrder: ['title', 'trainingSets'],
  });

  return (
    <PageContainer title={t('common.training')} backButton>
      {dialog}
      {training && (
        <div className={classes.trainingWrapper}>
          <form className={classes.trainingForm} onSubmit={formik.handleSubmit}>
            <Box paddingTop={1} display="flex" flexDirection="column" gap={1}>
              <AppFormTextField
                label={t('common.title')}
                fullWidth
                autoFocus
                name="title"
                formik={formik}
              />
              <AppFormTextField
                label={t('common.description')}
                fullWidth
                multiline
                minRows={3}
                maxRows={15}
                name="description"
                formik={formik}
              />
              {training?.status === TrainingStatus.notStarted && (
                <AppFormDateTimePicker
                  label={t('trainings.planedStartDate')}
                  name="planedStartDate"
                  formik={formik}
                />
              )}
              <TrainingActivityTypePicker formik={formik} fullWidth />
              {training?.status === TrainingStatus.notStarted && (
                <Box className={classes.createForOthersRow}>
                  {canCreateForOthers ? (
                    <Box className={classes.createForOthersToggle}>
                      <AppSwitchWithLabel
                        label={t('trainings.createForOthers')}
                        labelPlacement="end"
                        onChange={onCreateForOthersChange}
                        value={!!formik.values.createForOthers}
                        disabled={!canChangeOwner}
                      />
                    </Box>
                  ) : null}
                  <Box className={classes.createForOthersSelectWrap}>
                    <AppFormSelectWrapper
                      label={t('trainings.trainingOwner')}
                      name="trainingOwner"
                      fieldName="displayName"
                      options={performerSelectOptions}
                      formik={formik}
                      sortBy="displayName"
                      fullWidth
                      disabled={
                        !canCreateForOthers ||
                        !formik.values.createForOthers ||
                        !canChangeOwner
                      }
                      disableClear={
                        !canCreateForOthers ||
                        !formik.values.createForOthers ||
                        !canChangeOwner
                      }
                      renderOption={(row: IUser & { displayName?: string }) => (
                        <Box display="flex" gap={1} alignItems="center">
                          <AppUserAvatar
                            entity={row}
                            size={AvatarSize.s}
                            type={AvatarTypes.round}
                          />
                          <Typography variant="text-md">
                            {row.displayName ?? getUserViewName(row)}
                          </Typography>
                        </Box>
                      )}
                      renderValue={(selected: IUser | null) => {
                        const entity = selected ?? user;
                        return entity ? (
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            sx={{ minWidth: 0 }}>
                            <AppUserAvatar
                              entity={entity}
                              size={AvatarSize.s}
                              type={AvatarTypes.round}
                            />
                            <Typography variant="text-md" noWrap>
                              {getUserViewName(entity)}
                            </Typography>
                          </Box>
                        ) : null;
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>

            <Box marginTop={1} data-formik-field="trainingSets">
              {formik.values.trainingSets &&
                formik.values.trainingSets.length > 0 && (
                  <Box>
                    {Object.values(trainingSetGroups).map(
                      (trainingSetGroup, index) => (
                        <Box key={getSetKey(trainingSetGroup[0])} marginTop={1}>
                          <SetGroupEditTable
                            sets={trainingSetGroup}
                            useSetGroupEditTable={useSetGroupEditTable}
                          />
                        </Box>
                      ),
                    )}
                  </Box>
                )}
            </Box>

            <Box className={classes.addTrainingSetFooter}>
              <AppDashedBlockButton
                fullWidth
                onClick={() => onAddEmptyTrainingSet()}
                startIcon={<AddIcon />}
                yPadding={1}>
                {t('trainings.addTrainingSet')}
              </AppDashedBlockButton>
              {showUploadSetButton && (
                <AppDashedBlockButton
                  fullWidth
                  onClick={() => setShowSavedTrainingModal(true)}
                  startIcon={<FileUploadIcon />}
                  yPadding={1}>
                  {t('trainings.addSavedSet')}
                </AppDashedBlockButton>
              )}
            </Box>

            <div className={classes.trainingFooter}>
              <AppBarWithButtons
                loading={isLoading}
                disabled={isSubmitDisabled}
                onCancelClick={allowAndLeave}
              />
            </div>
          </form>
        </div>
      )}

      {showUploadSetButton && (
        <AppDialog
          open={!!showSavedTrainingModal}
          onClose={() => setShowSavedTrainingModal(false)}
          title={t('trainings.savedProgramSets')}>
          <SavedProgramSetSuperset
            onCancel={() => setShowSavedTrainingModal(false)}
            onSubmitSavedProgramSet={(value: {
              programSets: IProgramSet[];
              isSuperset: boolean;
            }) => {
              setShowSavedTrainingModal(false);
              onAddTrainingSetFromSavedProgramSet(
                value.programSets,
                value.isSuperset,
              );
            }}
          />
        </AppDialog>
      )}
    </PageContainer>
  );
}

export default withRouter(EditTraining);
